import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = loading, false = unauthed, obj = authed
    const [ready, setReady] = useState(false);

    const checkSession = useCallback(async () => {
        const token = localStorage.getItem("esm_token");
        if (!token) {
            setUser(false);
            setReady(true);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            localStorage.removeItem("esm_token");
            setUser(false);
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => {
        checkSession();
    }, [checkSession]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("esm_token", data.token);
        setUser(data.user);
        return data.user;
    }, []);

    // Called by the change-password flow: swap the stored token + user with the
    // rotated pair returned by the server.
    const applyLoginResponse = useCallback((data) => {
        if (!data) return;
        if (data.token) localStorage.setItem("esm_token", data.token);
        if (data.user) setUser(data.user);
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem("esm_token");
        setUser(false);
    }, []);

    return (
        <AuthContext.Provider value={{ user, ready, login, logout, applyLoginResponse, refresh: checkSession }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
